import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { SearchService } from '../searchbar/search.service';
import { Country } from '../../../../model/country.model';
import { Region } from '../../../../model/region.model';
import { District } from '../../../../model/district.model';

@Component({
    selector: 'formly-geonames-autocomplete',
    styles: [
        `
      ul.dropdown-list {
        list-style: none;
        margin: 0;
        padding: 0;
        background: white;
        border: 1px solid #ccc;
        max-height: 200px;
        overflow-y: auto;
        z-index: 10000;
      }
      ul.dropdown-list li {
        padding: 5px 10px;
        cursor: pointer;
      }
      ul.dropdown-list li:hover {
        background-color: #f0f0f0;
      }
    `,
    ],
    template: `
    <div class="row mb-3 d-flex gap-2">
      <div class="col-12 position-relative">
        <label class="form-label">{{'Country'}}</label>
        <input
          #countryInput
          type="text"
          class="form-control"
          [formControl]="countryCtrl"
          placeholder="{{'Search Country'}}"
          autocomplete="off"
        />
      </div>

      <div class="col-12 position-relative mt-md-0 mt-3">
        <label class="form-label">{{'Region'}}</label>
        <input
          #regionInput
          type="text"
          class="form-control"
          [formControl]="regionCtrl"
          placeholder="{{'Search Region'}}"
          [disabled]="!selectedCountry"
          autocomplete="off"
        />
      </div>

      <div class="col-12 position-relative mt-md-0 mt-3">
        <label class="form-label">{{'District'}}</label>
        <input
          #districtInput
          type="text"
          class="form-control"
          [formControl]="districtCtrl"
           placeholder="{{'Search District'}}"
          [disabled]="!selectedRegion"
          autocomplete="off"
        />
      </div>
    </div>
  `,
    standalone: false
})
export class FormlyGeonamesAutocompleteComponent
    extends FieldType
    implements OnInit, OnDestroy {

    countryCtrl = new FormControl('');
    regionCtrl = new FormControl('');
    districtCtrl = new FormControl('');

    filteredCountries$: Observable<any[]> = of([]);
    filteredRegions$: Observable<any[]> = of([]);
    filteredDistricts$: Observable<any[]> = of([]);

    allCountries: Country[] = [];
    allRegions: any[] = [];
    allDistricts: any[] = [];

    selectedCountry: Country | null | undefined = null;
    selectedRegion: Region | null = null;
    selectedDistrict: District | null = null;

    private countryDropdownEl!: HTMLElement;
    private regionDropdownEl!: HTMLElement;
    private districtDropdownEl!: HTMLElement;

    private blurTimeoutCountry: any;
    private blurTimeoutRegion: any;
    private blurTimeoutDistrict: any;

    private scrollListeners: Array<() => void> = [];
    private currentOpenDropdown: 'country' | 'region' | 'district' | null = null;

    @ViewChild('countryInput', { static: true }) countryInput!: ElementRef<HTMLInputElement>;
    @ViewChild('regionInput', { static: true }) regionInput!: ElementRef<HTMLInputElement>;
    @ViewChild('districtInput', { static: true }) districtInput!: ElementRef<HTMLInputElement>;

    constructor(private geoNames: SearchService, private renderer: Renderer2) {
        super();
    }

    async ngOnInit() {
        this.allCountries = await this.geoNames.getCountries();
        this.filteredCountries$ = of(this.allCountries);

        if (this.formControl.value) {
            await this.restoreSelections(this.formControl.value);
        }

        this.formControl.valueChanges.subscribe(async (value) => {
            if (value && typeof value === 'object') {
                await this.restoreSelections(value);
            }
        });

        this.countryCtrl.valueChanges.pipe(debounceTime(300)).subscribe((val) => {
            if (!val) {
                this.filteredCountries$ = of(this.allCountries);
                this.selectedCountry = null;
                this.regionCtrl.reset('');
                this.districtCtrl.reset('');
                this.selectedRegion = null;
                this.closeDropdown('country');
                return;
            }

            const match = this.allCountries.find((c) => c.name?.toLowerCase() === val.toLowerCase());
            if (match) {
                this.selectCountry(match);
                this.closeDropdown('country');
            } else {
                this.filteredCountries$ = of(
                    this.allCountries.filter((c) =>
                        c.name?.toLowerCase().includes(val.toLowerCase())
                    )
                );
                this.selectedCountry = null;
                this.regionCtrl.reset('');
                this.districtCtrl.reset('');
                this.selectedRegion = null;
                this.openDropdown('country');
            }
        });

        this.regionCtrl.valueChanges.pipe(debounceTime(300)).subscribe((val) => {
            if (!val) {
                this.filteredRegions$ = of(this.allRegions);
                this.selectedRegion = null;
                this.districtCtrl.reset('');
                this.closeDropdown('region');
                return;
            }

            const match = this.allRegions?.find((r) => r.name.toLowerCase() === val.toLowerCase());
            if (match) {
                this.selectRegion(match);
                this.closeDropdown('region');
            } else {
                this.filteredRegions$ = of(
                    this.allRegions?.filter((r) =>
                        r.name.toLowerCase().includes(val.toLowerCase())
                    )
                );
                this.selectedRegion = null;
                this.districtCtrl.reset('');
                this.openDropdown('region');
            }
        });

        this.districtCtrl.valueChanges.pipe(debounceTime(300)).subscribe((val) => {
            if (!val) {
                this.filteredDistricts$ = of(this.allDistricts);
                this.closeDropdown('district');
                return;
            }

            const match = this.allDistricts.find((d) => d.name.toLowerCase() === val.toLowerCase());
            if (match) {
                this.selectDistrict(match);
                this.closeDropdown('district');
            } else {
                this.filteredDistricts$ = of(
                    this.allDistricts.filter((d) =>
                        d.name.toLowerCase().includes(val.toLowerCase())
                    )
                );
                this.openDropdown('district');
            }
        });

        this.createDropdown('country');
        this.createDropdown('region');
        this.createDropdown('district');
        this.addInputListeners();
    }
      
      private async restoreSelections(value: any) {
        if (value.country && !this.selectedCountry) {
          this.selectedCountry = this.allCountries.find(c => c.id === value.country);
          if (this.selectedCountry) {
            this.countryCtrl.setValue(this.selectedCountry.name ?? '', { emitEvent: false });
            await this.loadRegions(Number(this.selectedCountry.id));
          }
        }
      
        if (value.region && this.allRegions.length) {
          this.selectedRegion = this.allRegions.find(r => r.id === value.region);
          if (this.selectedRegion) {
            this.regionCtrl.setValue(this.selectedRegion.name, { emitEvent: false });
            await this.loadDistricts(Number(this.selectedRegion.id));
          }
        }
      
        if (value.district && this.allDistricts.length) {
          this.selectedDistrict = this.allDistricts.find(d => d.id === value.district);
          if (this.selectedDistrict) {
            this.districtCtrl.setValue(this.selectedDistrict.name, { emitEvent: false });
          }
        }
    }

    private addInputListeners() {

        this.renderer.listen(this.countryInput.nativeElement, 'focus', () => {
            this.openDropdown('country');
        });
        this.renderer.listen(this.countryInput.nativeElement, 'blur', () => {
            this.blurTimeoutCountry = setTimeout(() => this.closeDropdown('country'), 200);
        });

        this.renderer.listen(this.regionInput.nativeElement, 'focus', () => {
            if (this.selectedCountry) this.openDropdown('region');
        });
        this.renderer.listen(this.regionInput.nativeElement, 'blur', () => {
            this.blurTimeoutRegion = setTimeout(() => this.closeDropdown('region'), 200);
        });

        this.renderer.listen(this.districtInput.nativeElement, 'focus', () => {
            if (this.selectedRegion) this.openDropdown('district');
        });
        this.renderer.listen(this.districtInput.nativeElement, 'blur', () => {
            this.blurTimeoutDistrict = setTimeout(() => this.closeDropdown('district'), 200);
        });
    }

    private createDropdown(type: 'country' | 'region' | 'district') {
    const dropdown = this.renderer.createElement('ul');
    this.renderer.addClass(dropdown, 'dropdown-list');
    this.renderer.setStyle(dropdown, 'position', 'absolute');
    this.renderer.setStyle(dropdown, 'display', 'none');
    this.renderer.setStyle(dropdown, 'z-index', '10001');

    this.renderer.appendChild(document.body, dropdown);

    this.renderer.listen(dropdown, 'mousedown', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const target = event.target as HTMLElement;
        if (target.tagName.toLowerCase() === 'li') {
            const selectedText = target.innerText;
            if (type === 'country') {
                const country = this.allCountries.find((c) => c.name === selectedText);
                if (country) this.selectCountry(country);
            } else if (type === 'region') {
                const region = this.allRegions.find((r) => r.name === selectedText);
                if (region) this.selectRegion(region);
            } else if (type === 'district') {
                const district = this.allDistricts.find((d) => d.name === selectedText);
                if (district) this.selectDistrict(district);
            }
            this.closeDropdown(type);
        }
    });

    this.renderer.listen(dropdown, 'click', (event: MouseEvent) => {
        event.stopPropagation();
    });

    if (type === 'country') this.countryDropdownEl = dropdown;
    else if (type === 'region') this.regionDropdownEl = dropdown;
    else this.districtDropdownEl = dropdown;
}

    private findScrollableAncestors(element: HTMLElement): HTMLElement[] {
        const scrollableAncestors: HTMLElement[] = [];
        let parent = element.parentElement;
    
        while (parent && parent !== document.body) {
            const computedStyle = window.getComputedStyle(parent);
            if (computedStyle.overflow === 'auto' || 
                computedStyle.overflow === 'scroll' || 
                computedStyle.overflowY === 'auto' || 
                computedStyle.overflowY === 'scroll') {
                scrollableAncestors.push(parent);
            }
            parent = parent.parentElement;
        }
    
        scrollableAncestors.push(window as any);
    
        return scrollableAncestors;
    }
    
    private addScrollListeners(inputEl: HTMLElement) {
        this.removeScrollListeners();
    
        const scrollableElements = this.findScrollableAncestors(inputEl);
        
        scrollableElements.forEach(scrollableEl => {
            const listener = this.renderer.listen(scrollableEl, 'scroll', () => {
                if (this.currentOpenDropdown) {
                    this.updateDropdownPosition(this.currentOpenDropdown);
                }
            });
            this.scrollListeners.push(listener);
        });
    }
    
    private removeScrollListeners() {
        this.scrollListeners.forEach(removeListener => removeListener());
        this.scrollListeners = [];
    }
    
    private updateDropdownPosition(type: 'country' | 'region' | 'district') {
        let dropdown: HTMLElement;
        let inputEl: HTMLElement;
    
        if (type === 'country') {
            dropdown = this.countryDropdownEl;
            inputEl = this.countryInput.nativeElement;
        } else if (type === 'region') {
            dropdown = this.regionDropdownEl;
            inputEl = this.regionInput.nativeElement;
        } else {
            dropdown = this.districtDropdownEl;
            inputEl = this.districtInput.nativeElement;
        }
    
        const rect = inputEl.getBoundingClientRect();
        
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
            this.renderer.setStyle(dropdown, 'display', 'none');
            return;
        }
    
        this.renderer.setStyle(dropdown, 'top', `${rect.bottom + window.scrollY}px`);
        this.renderer.setStyle(dropdown, 'left', `${rect.left + window.scrollX}px`);
        this.renderer.setStyle(dropdown, 'width', `${rect.width}px`);
    }

    private openDropdown(type: 'country' | 'region' | 'district') {
        let dropdown: HTMLElement;
        let inputEl: HTMLElement;
        let items: any[] = [];

        if (type === 'country') {
            dropdown = this.countryDropdownEl;
            inputEl = this.countryInput.nativeElement;
            this.filteredCountries$.subscribe((data) => {
                items = data;
                this.renderDropdownItems(dropdown, items);
            });
        } else if (type === 'region') {
            dropdown = this.regionDropdownEl;
            inputEl = this.regionInput.nativeElement;
            this.filteredRegions$.subscribe((data) => {
                items = data;
                this.renderDropdownItems(dropdown, items);
            });
        } else {
            dropdown = this.districtDropdownEl;
            inputEl = this.districtInput.nativeElement;
            this.filteredDistricts$.subscribe((data) => {
                items = data;
                this.renderDropdownItems(dropdown, items);
            });
        }

        this.currentOpenDropdown = type;
        this.addScrollListeners(inputEl);

        const rect = inputEl.getBoundingClientRect();
        this.renderer.setStyle(dropdown, 'top', `${rect.bottom + window.scrollY}px`);
        this.renderer.setStyle(dropdown, 'left', `${rect.left + window.scrollX}px`);
        this.renderer.setStyle(dropdown, 'width', `${rect.width}px`);
        this.renderer.setStyle(dropdown, 'display', 'block');
    }

    private closeDropdown(type: 'country' | 'region' | 'district') {
        if (type === 'country' && this.countryDropdownEl) {
            this.renderer.setStyle(this.countryDropdownEl, 'display', 'none');
            if (this.blurTimeoutCountry) clearTimeout(this.blurTimeoutCountry);
        } else if (type === 'region' && this.regionDropdownEl) {
            this.renderer.setStyle(this.regionDropdownEl, 'display', 'none');
            if (this.blurTimeoutRegion) clearTimeout(this.blurTimeoutRegion);
        } else if (type === 'district' && this.districtDropdownEl) {
            this.renderer.setStyle(this.districtDropdownEl, 'display', 'none');
            if (this.blurTimeoutDistrict) clearTimeout(this.blurTimeoutDistrict);
        }

        if (this.currentOpenDropdown === type) {
            this.currentOpenDropdown = null;
            this.removeScrollListeners();
        }
    }

    private renderDropdownItems(dropdown: HTMLElement, items?: any[]) {
        dropdown.innerHTML = '';

        if (!items?.length) {
            const noItem = this.renderer.createElement('li');
            this.renderer.setProperty(noItem, 'innerText', 'No results');
            this.renderer.setStyle(noItem, 'color', '#999');
            dropdown.appendChild(noItem);
            return;
        }

        items?.forEach((item) => {
            const li = this.renderer.createElement('li');
            this.renderer.setProperty(li, 'innerText', item.name);
            dropdown.appendChild(li);
        });
    }

    private selectCountry(country: Country) {
        this.selectedCountry = country;
        this.formControl.setValue({ country: this.selectedCountry.id });

        this.countryCtrl.setValue(country.name ?? null, { emitEvent: false });

        this.selectedRegion = null;
        this.regionCtrl.reset('');
        this.districtCtrl.reset('');
        this.allRegions = [];
        this.allDistricts = [];

        this.loadRegions(Number(country.id));
    }

    private selectRegion(region: Region) {
        this.selectedRegion = region;
        this.formControl.setValue({ country: this.selectedCountry?.id, region: this.selectedRegion.id });

        this.regionCtrl.setValue(region.name, { emitEvent: false });

        this.districtCtrl.reset('');
        this.allDistricts = [];

        this.loadDistricts(Number(region.id));
    }

    private async loadRegions(countryId: number) {
        this.allRegions = await this.geoNames.getRegions(countryId.toString());
        this.filteredRegions$ = of(this.allRegions);
    }

    private async loadDistricts(regionId: number) {
        this.allDistricts = await  this.geoNames.getDistrict(regionId.toString());
        this.filteredDistricts$ = of(this.allDistricts);
    }

    private selectDistrict(district: District) {
        this.districtCtrl.setValue(district.name, { emitEvent: false });
        this.selectedDistrict = district;
        this.formControl.setValue({ country: this.selectedCountry?.id, region: this.selectedRegion?.id, district: this.selectedDistrict.id });

    }


    ngOnDestroy() {
        this.removeScrollListeners();

        if (this.countryDropdownEl) this.renderer.removeChild(document.body, this.countryDropdownEl);
        if (this.regionDropdownEl) this.renderer.removeChild(document.body, this.regionDropdownEl);
        if (this.districtDropdownEl) this.renderer.removeChild(document.body, this.districtDropdownEl);

        if (this.blurTimeoutCountry) clearTimeout(this.blurTimeoutCountry);
        if (this.blurTimeoutRegion) clearTimeout(this.blurTimeoutRegion);
        if (this.blurTimeoutDistrict) clearTimeout(this.blurTimeoutDistrict);
    }
}